import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import EmailConfirmationModal from '../components/EmailConfirmationModal';

// Combine the structured address parts into a single comma-separated string.
// Kept in sync with the legacy single-string fields (e.g. homeAddress, businessAddress)
// so existing readers continue to work without changes.
const combineAddress = (parts) => {
  if (!parts) return '';
  const { line1 = '', line2 = '', city = '', state = '', zip = '' } = parts;
  const lineCombo = [line1, line2].filter(Boolean).join(', ');
  const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [lineCombo, cityStateZip].filter(Boolean).join(', ');
};

const normalizeFounders = (value) => {
  if (Array.isArray(value)) return value;
  let current = value;
  for (let i = 0; i < 4 && typeof current === 'string'; i += 1) {
    try {
      current = JSON.parse(current);
    } catch (e) {
      return [];
    }
  }
  return Array.isArray(current) ? current : [];
};

// Small reusable group of structured address inputs used across the formation form.
const AddressFieldGroup = ({ prefix, values, onChange, disabled, required }) => {
  const handle = (key) => (e) => onChange(`${prefix}${key}`, e.target.value);
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
      <div className="md:col-span-6">
        <label className="block text-xs font-medium text-gray-600 mb-1">Address Line 1{required ? ' *' : ''}</label>
        <input type="text" className="input-field" value={values.line1 || ''} onChange={handle('Line1')} disabled={disabled} />
      </div>
      <div className="md:col-span-6">
        <label className="block text-xs font-medium text-gray-600 mb-1">Address Line 2 (optional)</label>
        <input type="text" className="input-field" value={values.line2 || ''} onChange={handle('Line2')} disabled={disabled} />
      </div>
      <div className="md:col-span-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">City{required ? ' *' : ''}</label>
        <input type="text" className="input-field" value={values.city || ''} onChange={handle('City')} disabled={disabled} />
      </div>
      <div className="md:col-span-1">
        <label className="block text-xs font-medium text-gray-600 mb-1">State{required ? ' *' : ''}</label>
        <input type="text" className="input-field" value={values.state || ''} onChange={handle('State')} disabled={disabled} />
      </div>
      <div className="md:col-span-2">
        <label className="block text-xs font-medium text-gray-600 mb-1">ZIP Code{required ? ' *' : ''}</label>
        <input type="text" className="input-field" value={values.zip || ''} onChange={handle('Zip')} disabled={disabled} />
      </div>
    </div>
  );
};

const CompanyFormation = ({ userInfo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Founder information (pre-populated)
    founders: [
      {
        fullName: '',
        email: '',
        cellPhone: '',
        homeAddress: '',
        dateOfBirth: '',
        ssn: '',
        isInvited: false // First founder is the current user
      }
    ],
    
    // Company formation status
    companyAlreadyFormed: null, // true/false
    
    // For existing companies
    companyName: '',
    dateOfFormation: '',
    einNumber: '',
    businessAddress: '',
    businessDescription: '',
    naicsCodes: '',
    preferredDomains: ['', '', ''],
    taxReturnsFiledPreviously: null,
    hasBankAccount: null, // true/false - whether company has existing bank account
    bankInfo: {
      bankName: '',
      routingNumber: '',
      accountNumber: '',
      bankAddress: '',
      bankPhone: ''
    },
    
    // For new companies
    desiredCompanyName: '',
    newCompanyBusinessAddress: '',
    newCompanyBusinessDescription: '',
    newCompanyNaicsCodes: '',
    newCompanyPreferredDomains: ['', '', ''],
    
    // Document uploads
    certificateOfFormation: null,
    ss4Document: null,
    boirDocument: null
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [submissionId, setSubmissionId] = useState('');
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [isFormLocked, setIsFormLocked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Admins see the form locked by default; toggling this unlocks editing.
  const [adminEditMode, setAdminEditMode] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(''); // Track if submission is 'draft' or 'submitted'
  const [emailConfirmation, setEmailConfirmation] = useState({
    isOpen: false,
    emailData: null,
    onConfirm: null
  });
  const [ssnVisible, setSsnVisible] = useState({});
  const [showDifferentBusinessAddress, setShowDifferentBusinessAddress] = useState(false);
  const foundersArray = normalizeFounders(formData.founders);

  // Pre-populate founder information from userInfo
  useEffect(() => {
    // Check if user is admin or in admin edit mode
    const adminStatus = userInfo?.role === 'admin' ||
      userInfo?.role === 'superadmin' ||
      userInfo?.accountType === 'admin' ||
      userInfo?.accountType === 'superadmin' ||
      location.state?.adminEdit === true;

    setIsAdmin(adminStatus);

    if (userInfo && !adminStatus) {
      // For the initial founder, pre-fill their email
      // Only do this if there are no founders yet (to avoid overwriting existing data)
      setFormData(prev => {
        if (prev.founders.length === 0 || (prev.founders.length === 1 && !prev.founders[0].email)) {
          return {
            ...prev,
            founders: [{
              fullName: userInfo.name || '',
              email: userInfo.email || '',
              cellPhone: '',
              homeAddress: '',
              dateOfBirth: '',
              ssn: '',
              isInvited: false
            }]
          };
        }
        return prev;
      });
    }
  }, [userInfo, location.state]);

  // Mirror the first founder's home address into the new-company business
  // address fields. Used when the user has NOT opted to use a different business
  // address (on load, on toggle-off, and when the home address is edited).
  const mirrorHomeToBusinessAddress = (founder) => {
    const first = founder || (formData.founders && formData.founders[0]) || {};
    const line1 = first.homeAddressLine1 || '';
    const line2 = first.homeAddressLine2 || '';
    const city = first.homeAddressCity || '';
    const state = first.homeAddressState || '';
    const zip = first.homeAddressZip || '';
    setFormData(prev => ({
      ...prev,
      newCompanyBusinessAddressLine1: line1,
      newCompanyBusinessAddressLine2: line2,
      newCompanyBusinessAddressCity: city,
      newCompanyBusinessAddressState: state,
      newCompanyBusinessAddressZip: zip,
      newCompanyBusinessAddress: combineAddress({ line1, line2, city, state, zip })
    }));
  };

  // Toggle the "use a different business address" override. When turning it OFF,
  // re-mirror the founder's home address so the fields reflect that choice.
  const toggleDifferentBusinessAddress = () => {
    setShowDifferentBusinessAddress(prev => {
      const next = !prev;
      if (!next) mirrorHomeToBusinessAddress();
      return next;
    });
  };

  // Check for existing submission on component mount
  useEffect(() => {
    const checkExistingSubmission = async () => {
      if (!userInfo) return;

      // Determine admin status here to ensure it's set before making API calls
      const adminStatus = userInfo?.role === 'admin' ||
        userInfo?.role === 'superadmin' ||
        userInfo?.accountType === 'admin' ||
        userInfo?.accountType === 'superadmin' ||
        location.state?.adminEdit === true;

      // Helper function to merge founders from user management
      const mergeFoundersFromUserManagement = async (existingFounders) => {
        console.log("MERGING");
        try {
          // Fetch user management data to check for additional founders
          const companyName = userInfo?.companyName;
          if (!companyName) return existingFounders;

          const response = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users`
          );

          if (response.ok) {
            const data = await response.json();
            const founderUsers = (data.users || []).filter(u => u.role === 'founder');

            // Merge founders from user management with existing founders
            const founderEmails = new Set(existingFounders.map(f => f.email));

            console.log("founder users:", founderUsers);

            founderUsers.forEach(founderUser => {
              if (!founderEmails.has(founderUser.userEmail)) {
                // Add founder from user management as invited
                existingFounders.push({
                  email: founderUser.userEmail,
                  fullName: founderUser.userName || '',
                  cellPhone: '',
                  homeAddress: '',
                  dateOfBirth: '',
                  ssn: '',
                  isInvited: founderUser.personalInfoSubmitted !== 'true',
                  fromUserManagement: true
                });
                console.log(`Added founder ${founderUser.userEmail} from user management`);
              }
            });

            console.log("updated founders after merge:", existingFounders);
          }
        } catch (error) {
          console.error('Error fetching user management data:', error);
        }
        return existingFounders;
      };

      const populateFormFromSubmission = async (submission) => {
        console.log("Raw submission data:", submission);

        // Handle different field naming from CSV vs JavaScript
        const companyDataField = submission['Company Data JSON'] || submission.companyData || '{}';
        const foundersField = submission['Founders JSON'] || submission.founders || '[]';
        const companyAlreadyFormedField = submission['Company Already Formed'] ?? submission.companyAlreadyFormed;
        const statusField = submission['Status'] || submission.status || 'submitted';

        console.log("Extracted fields:", { companyDataField, foundersField, companyAlreadyFormedField, statusField });

        let companyData = {};
        let founders = [];

        // Parse company data
        if (typeof companyDataField === 'string') {
          try {
            companyData = JSON.parse(companyDataField);
          } catch (e) {
            console.error('Error parsing company data JSON:', e);
          }
        } else {
          companyData = companyDataField || {};
        }

        // Parse founders data
        if (typeof foundersField === 'string') {
          try {
            founders = normalizeFounders(foundersField);
          } catch (e) {
            console.error('Error parsing founders JSON:', e);
          }
        } else {
          founders = normalizeFounders(foundersField);
        }

        // Merge with founders from user management
        founders = await mergeFoundersFromUserManagement(founders);
        founders = normalizeFounders(founders);

        // Parse company already formed status
        let companyAlreadyFormed = companyAlreadyFormedField;
        if (typeof companyAlreadyFormedField === 'string') {
          companyAlreadyFormed = companyAlreadyFormedField === 'true';
        }

        console.log("Parsed data:", { companyData, founders, companyAlreadyFormed, status: statusField });

        setFormData(prev => ({
          ...prev,
          // Company formation status
          companyAlreadyFormed: companyAlreadyFormed,

          // Founders
          founders: founders.length > 0 ? founders : prev.founders,

          // Existing company fields
          companyName: companyData.companyName || '',
          dateOfFormation: companyData.dateOfFormation || '',
          einNumber: companyData.einNumber || '',
          businessAddress: companyData.businessAddress || '',
          businessAddressLine1: companyData.businessAddressLine1 || '',
          businessAddressLine2: companyData.businessAddressLine2 || '',
          businessAddressCity: companyData.businessAddressCity || '',
          businessAddressState: companyData.businessAddressState || '',
          businessAddressZip: companyData.businessAddressZip || '',
          businessDescription: companyData.businessDescription || '',
          naicsCodes: companyData.naicsCodes || '',
          preferredDomains: companyData.preferredDomains || ['', '', ''],
          taxReturnsFiledPreviously: companyData.taxReturnsFiledPreviously,
          hasBankAccount: companyData.hasBankAccount,
          bankInfo: companyData.bankInfo || {
            bankName: '',
            routingNumber: '',
            accountNumber: '',
            bankAddress: '',
            bankPhone: ''
          },

          // New company fields
          desiredCompanyName: companyData.desiredCompanyName || '',
          newCompanyBusinessAddress: companyData.newCompanyBusinessAddress || '',
          newCompanyBusinessAddressLine1: companyData.newCompanyBusinessAddressLine1 || '',
          newCompanyBusinessAddressLine2: companyData.newCompanyBusinessAddressLine2 || '',
          newCompanyBusinessAddressCity: companyData.newCompanyBusinessAddressCity || '',
          newCompanyBusinessAddressState: companyData.newCompanyBusinessAddressState || '',
          newCompanyBusinessAddressZip: companyData.newCompanyBusinessAddressZip || '',
          newCompanyBusinessDescription: companyData.newCompanyBusinessDescription || '',
          newCompanyNaicsCodes: companyData.newCompanyNaicsCodes || '',
          newCompanyPreferredDomains: companyData.newCompanyPreferredDomains || ['', '', ''],

          // Documents
          certificateOfFormation: companyData.certificateOfFormation || null,
          ss4Document: companyData.ss4Document || null,
          boirDocument: companyData.boirDocument || null
        }));

        // New-company path: decide whether a distinct business address was saved.
        // If so, reveal the "different business address" override so it isn't
        // overwritten. Otherwise mirror the founder's home address into the
        // business fields so the last page is populated (and persists) correctly.
        if (companyAlreadyFormed === false) {
          const first = (founders && founders[0]) || {};
          const biz = [
            companyData.newCompanyBusinessAddressLine1,
            companyData.newCompanyBusinessAddressCity,
            companyData.newCompanyBusinessAddressState,
            companyData.newCompanyBusinessAddressZip
          ].map(v => (v || '').trim());
          const home = [
            first.homeAddressLine1,
            first.homeAddressCity,
            first.homeAddressState,
            first.homeAddressZip
          ].map(v => (v || '').trim());
          if (biz.some(Boolean) && JSON.stringify(biz) !== JSON.stringify(home)) {
            setShowDifferentBusinessAddress(true);
          } else {
            setShowDifferentBusinessAddress(false);
            mirrorHomeToBusinessAddress(first);
          }
        }

        // Return the status so we can check if this is a draft or submitted
        return statusField;
      };

      try {
        // If in admin edit mode, load specific submission
        if (location.state?.adminEdit && location.state?.submissionId) {
          const response = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/submission/${location.state.submissionId}`
          );

          if (response.ok) {
            const submission = await response.json();
            setSubmissionId(submission.id);
            const status = await populateFormFromSubmission(submission);
            setSubmissionStatus(status); // Track the status
            // Only lock the form if the status is 'submitted', not 'draft'
            setIsFormLocked(status === 'submitted');

            // Set appropriate step based on data
            if (submission.companyAlreadyFormed !== null) {
              setCurrentStep(3);
            } else {
              setCurrentStep(2);
            }
          }
        } else if (adminStatus) {
          // For regular admin access (not from admin dashboard), get ALL submissions for this company
          console.log("Admin accessing company formation - getting all company submissions");
          const response = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/submissions`
          );

          if (response.ok) {
            const submissions = await response.json();
            console.log("All submissions for admin:", submissions);
            if (submissions && submissions.length > 0) {
              // Filter submissions to only include those with valid IDs for admin too
              const validSubmissions = submissions.filter(s => {
                const hasValidId = (s.ID && s.ID.trim() !== '') || (s.id && s.id.trim() !== '');
                return hasValidId;
              });

              // Sort submissions by submission date (newest first)
              const sortedSubmissions = validSubmissions.sort((a, b) =>
                new Date(b['Submitted At']) - new Date(a['Submitted At'])
              );

              console.log("Valid, sorted submissions for admin:", sortedSubmissions);

              // For admin, use the latest valid submission
              if (sortedSubmissions.length > 0) {
                const latestSubmission = sortedSubmissions[0];
                console.log("Latest submission for admin:", latestSubmission);
                setSubmissionId(latestSubmission.ID || latestSubmission.id);
                const status = await populateFormFromSubmission(latestSubmission);
                setSubmissionStatus(status); // Track the status
                // Only lock the form if the status is 'submitted', not 'draft'
                setIsFormLocked(status === 'submitted');

                // Start admins on page 1
                setCurrentStep(1);
              } else {
                console.log("No submissions found for admin - showing empty form");
                // If no submissions found, admin sees empty form
                setCurrentStep(1);
              }
            }
          }
        } else {
          // For founders, get latest company submission (same as admin logic)
          console.log("Founder accessing company formation - getting latest company submission");
          const response = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/submissions`
          );

          if (response.ok) {
            const submissions = await response.json();
            console.log("All submissions for founder:", submissions);
            if (submissions && submissions.length > 0) {
              // Filter submissions to only include those with valid IDs, then sort by submission date (newest first)
              const validSubmissions = submissions.filter(s => {
                const hasValidId = (s.ID && s.ID.trim() !== '') || (s.id && s.id.trim() !== '');
                const hasFounders = (s['Founders JSON'] && s['Founders JSON'] !== '[]') || (s.founders && s.founders.length > 0);
                return hasValidId && hasFounders;
              });

              const sortedSubmissions = validSubmissions.sort((a, b) =>
                new Date(b.submittedAt || b['Submitted At']) - new Date(a.submittedAt || a['Submitted At'])
              );

              if (sortedSubmissions.length === 0) {
                console.error('❌ No valid submissions found with IDs and founders');
                return;
              }

              // Use the latest valid submission for the company
              const latestSubmission = sortedSubmissions[0];
              console.log("Latest submission for founder:", latestSubmission);

              // Set submission ID - should always be valid now due to filtering
              const submissionId = latestSubmission.ID || latestSubmission.id;
              console.log("Setting submission ID:", submissionId);
              if (!submissionId) {
                console.error("❌ No valid submission ID found even after filtering");
                return;
              }
              setSubmissionId(submissionId);
              const status = await populateFormFromSubmission(latestSubmission);
              setSubmissionStatus(status); // Track the status

              // Check if current founder has personal info in the submission
              const foundersField = latestSubmission['Founders JSON'] || latestSubmission.founders || '[]';
              let founders = [];
              try {
                founders = normalizeFounders(foundersField);
              } catch (e) {
                console.error('Error parsing founders JSON:', e);
              }

              // Check if current user has their information in the founders list
              const currentUserEmail = userInfo?.email || userInfo?.username;
              const currentFounderInfo = founders.find(f => f.email === currentUserEmail);

              console.log("Current user email:", currentUserEmail);
              console.log("founders from submission:", founders);
              console.log("Current founder info from submission:", currentFounderInfo);

              console.log("Status: ", status);
              // If founder doesn't have personal info filled out, allow editing step 1
              // OR if the status is 'draft', allow editing
              const isDraft = status === 'draft';
              if (isDraft || !currentFounderInfo || !currentFounderInfo.fullName || !currentFounderInfo.cellPhone || !currentFounderInfo.dateOfBirth || !currentFounderInfo.homeAddress) {
                console.log("Current founder missing personal info or form is draft, allowing step 1 editing");
                setIsFormLocked(false); // Allow editing for personal info or drafts
                setCurrentStep(1); // Start at founder information step

                // If founder exists but is incomplete, update their info
                if (currentFounderInfo) {
                  setFormData(prev => ({
                    ...prev,
                    founders: prev.founders.map(founder =>
                      founder.email === currentUserEmail ? {
                        ...founder,
                        fullName: currentFounderInfo.fullName || '',
                        email: currentFounderInfo.email || founder.email,
                        cellPhone: currentFounderInfo.cellPhone || '',
                        homeAddress: currentFounderInfo.homeAddress || '',
                        dateOfBirth: currentFounderInfo.dateOfBirth || '',
                        isInvited: true
                      } : founder
                    )
                  }));
                } else {
                  // If founder doesn't exist in the list, set up the first founder slot for them
                  console.log("Adding current founder to founders list for editing");
                  setFormData(prev => {
                    const firstFounder = prev.founders[0];
                    const shouldUpdateFirstFounder = !firstFounder.email || firstFounder.email === currentUserEmail;

                    if (shouldUpdateFirstFounder) {
                      return {
                        ...prev,
                        founders: [{
                          ...prev.founders[0],
                          email: currentUserEmail || '',
                          isInvited: false
                        }, ...prev.founders.slice(1)]
                      };
                    }
                    return prev; // Return unchanged if shouldn't update
                  });
                }
              } else {
                // For founders, allow navigation through all steps but lock editing except personal info
                // Only lock if status is 'submitted', not 'draft'
                setIsFormLocked(status === 'submitted'); // Lock form if founder info is complete and submitted
                // Start at step 1 so founder can see all founder info, but allow navigation
                setCurrentStep(1);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error checking existing submission:', error);
      }
    };

    checkExistingSubmission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, location.state]);

  const handleInputChange = (field, value) => {
    // Allow founders to edit on step 1 when form is unlocked for personal info editing
    const isFounderEditingPersonalInfo = currentStep === 1 && userInfo?.accountType === 'founder' && !isFormLocked;

    // Prevent changes if form is locked and user is not admin, unless founder editing personal info
    if ((isFormLocked && !isAdmin) && !isFounderEditingPersonalInfo) {
      return;
    }

    // Admins are read-only unless they explicitly entered edit mode
    if (isAdmin && !adminEditMode) {
      return;
    }

    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Keep legacy single-string address fields in sync with the structured sub-fields.
      const syncMap = {
        businessAddress: ['businessAddressLine1', 'businessAddressLine2', 'businessAddressCity', 'businessAddressState', 'businessAddressZip'],
        newCompanyBusinessAddress: ['newCompanyBusinessAddressLine1', 'newCompanyBusinessAddressLine2', 'newCompanyBusinessAddressCity', 'newCompanyBusinessAddressState', 'newCompanyBusinessAddressZip'],
      };
      Object.entries(syncMap).forEach(([target, partKeys]) => {
        if (partKeys.includes(field)) {
          next[target] = combineAddress({
            line1: next[partKeys[0]] || '',
            line2: next[partKeys[1]] || '',
            city: next[partKeys[2]] || '',
            state: next[partKeys[3]] || '',
            zip: next[partKeys[4]] || ''
          });
        }
      });

      // When the user chooses "need help forming a company" (companyAlreadyFormed
      // === false) and hasn't opted for a different business address, mirror the
      // first founder's home address into the business address fields. This
      // handles the common case where the home address was entered on step 1
      // before this choice was made, so the last page shows it on first load.
      if (field === 'companyAlreadyFormed' && value === false && !showDifferentBusinessAddress) {
        const first = (next.founders && next.founders[0]) || {};
        next.newCompanyBusinessAddressLine1 = first.homeAddressLine1 || '';
        next.newCompanyBusinessAddressLine2 = first.homeAddressLine2 || '';
        next.newCompanyBusinessAddressCity = first.homeAddressCity || '';
        next.newCompanyBusinessAddressState = first.homeAddressState || '';
        next.newCompanyBusinessAddressZip = first.homeAddressZip || '';
        next.newCompanyBusinessAddress = combineAddress({
          line1: first.homeAddressLine1 || '',
          line2: first.homeAddressLine2 || '',
          city: first.homeAddressCity || '',
          state: first.homeAddressState || '',
          zip: first.homeAddressZip || ''
        });
      }

      return next;
    });
  };

  // Helper function to determine if inputs should be disabled
  const isInputDisabled = (additionalCondition = false) => {
    // Admins see the form locked by default; require explicit edit mode to modify.
    if (isAdmin && !adminEditMode) {
      return true;
    }

    // If this is a draft, allow editing (unless additional condition prevents it)
    if (submissionStatus === 'draft' && !additionalCondition) {
      return false;
    }

    // For founders, handle personal info editing permissions
    if (userInfo?.accountType === 'founder') {
      // If on step 1 and personal info is already complete and submitted, lock it
      const currentUserEmail = userInfo?.email || userInfo?.username;
      const currentFounder = foundersArray.find(f => f.email === currentUserEmail);
      const hasSubmittedPersonalInfo = currentFounder && !currentFounder.isInvited && isFounderPersonalInfoComplete();

      if (currentStep === 1 && submissionId && hasSubmittedPersonalInfo) {
        return true; // Lock personal info once it's been submitted
      }
      // If on step 1 and personal info is not complete, allow editing
      if (currentStep === 1) {
        return false;
      }

      if (!submissionId) {
        return false;
      }
      // Lock other steps if form is fully submitted
      return submissionStatus === 'submitted';
    }

    // For non-founders, use original logic
    const isFounderEditingPersonalInfo = currentStep === 1 && userInfo?.accountType === 'founder' && !isFormLocked;
    return ((isFormLocked && !isAdmin) || additionalCondition) && !isFounderEditingPersonalInfo;
  };

  // Helper function to determine if navigation should be disabled
  const isNavigationDisabled = () => {
    // Allow founders to navigate between steps even when form is locked
    if (userInfo?.accountType === 'founder') {
      return false; // Founders can always navigate
    }
    return isFormLocked && !isAdmin;
  };

  // Helper function to check if founder has completed their personal info
  const isFounderPersonalInfoComplete = () => {
    if (userInfo?.accountType !== 'founder') return true;

    const currentUserEmail = userInfo?.email || userInfo?.username;
    const currentFounder = foundersArray.find(f => f.email === currentUserEmail);

    if (!currentFounder) return false;

    // Check if fields are filled AND founder has submitted (not invited anymore)
    return !!(currentFounder.fullName &&
              currentFounder.cellPhone &&
              currentFounder.dateOfBirth &&
              currentFounder.homeAddress &&
              currentFounder.ssn &&
              !currentFounder.isInvited); // Only complete after they've submitted
  };

  const handleFounderChange = (index, field, value) => {
    // Check if this founder can be edited
    const currentUserEmail = userInfo?.email || userInfo?.username;
    const founder = foundersArray[index];
    const isCurrentUser = founder.email === currentUserEmail;
    // Admins are read-only until they explicitly enter edit mode.
    const canEditFounder = !isFormLocked || (isAdmin && adminEditMode) || (userInfo?.accountType === 'founder' && isCurrentUser);

    // Prevent changes if not allowed to edit this founder
    if (!canEditFounder) {
      return;
    }

    // Don't allow founders to edit their own email
    if (field === 'email' && isCurrentUser && userInfo?.accountType === 'founder') {
      return;
    }

    const updatedFounders = [...foundersArray];
    updatedFounders[index] = {
      ...updatedFounders[index],
      [field]: value
    };
    // Keep legacy `homeAddress` string in sync with structured sub-fields
    const homePartKeys = ['homeAddressLine1', 'homeAddressLine2', 'homeAddressCity', 'homeAddressState', 'homeAddressZip'];
    if (homePartKeys.includes(field)) {
      const f = updatedFounders[index];
      f.homeAddress = combineAddress({
        line1: f.homeAddressLine1 || '',
        line2: f.homeAddressLine2 || '',
        city: f.homeAddressCity || '',
        state: f.homeAddressState || '',
        zip: f.homeAddressZip || ''
      });
    }
    setFormData(prev => {
      const next = { ...prev, founders: updatedFounders };
      // For "need help forming a company" (companyAlreadyFormed === false), if the
      // founder is the first one and the user has NOT chosen to use a different
      // business address, mirror their structured home address into the new-company
      // business address fields automatically.
      const isFirstFounder = index === 0;
      if (isFirstFounder && next.companyAlreadyFormed === false && !showDifferentBusinessAddress && homePartKeys.includes(field)) {
        next.newCompanyBusinessAddressLine1 = updatedFounders[index].homeAddressLine1 || '';
        next.newCompanyBusinessAddressLine2 = updatedFounders[index].homeAddressLine2 || '';
        next.newCompanyBusinessAddressCity = updatedFounders[index].homeAddressCity || '';
        next.newCompanyBusinessAddressState = updatedFounders[index].homeAddressState || '';
        next.newCompanyBusinessAddressZip = updatedFounders[index].homeAddressZip || '';
        next.newCompanyBusinessAddress = updatedFounders[index].homeAddress || '';
      }
      return next;
    });
  };

  const addFounder = () => {
    // Prevent changes if form is locked and user is not admin
    if (isFormLocked && !isAdmin) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      founders: [
        ...prev.founders,
        {
          fullName: '',
          email: '',
          cellPhone: '',
          homeAddress: '',
          dateOfBirth: '',
          ssn: '',
          isInvited: true // Mark as invited founder - only email required initially
        }
      ]
    }));
  };

  const removeFounder = (index) => {
    // Prevent changes if form is locked and user is not admin
    if (isFormLocked && !isAdmin) {
      return;
    }

    if (foundersArray.length > 1) {
      const updatedFounders = foundersArray.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        founders: updatedFounders
      }));
    }
  };

  const handleBankInfoChange = (field, value) => {
    // Prevent changes if form is locked and user is not admin
    if (isFormLocked && !isAdmin) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      bankInfo: {
        ...prev.bankInfo,
        [field]: value
      }
    }));
  };

  const handleDomainChange = (index, value) => {
    // Prevent changes if form is locked and user is not admin
    if (isFormLocked && !isAdmin) {
      return;
    }

    const updatedDomains = [...formData.preferredDomains];
    updatedDomains[index] = value;
    setFormData(prev => ({
      ...prev,
      preferredDomains: updatedDomains
    }));
  };

  const handleNewCompanyDomainChange = (index, value) => {
    // Prevent changes if form is locked and user is not admin
    if (isFormLocked && !isAdmin) {
      return;
    }

    const updatedDomains = [...formData.newCompanyPreferredDomains];
    updatedDomains[index] = value;
    setFormData(prev => ({
      ...prev,
      newCompanyPreferredDomains: updatedDomains
    }));
  };

  const handleFileDownload = async (fileUrl, fileName) => {
    try {
      // Extract the file path from the GCS URL
      const filePath = fileUrl.replace(`gs://${process.env.REACT_APP_GCS_BUCKET_NAME || 'streamlaunched_serviceaccoun_userbucket'}/`, '');

      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/download/${encodeURIComponent(filePath)}`,
        {
          responseType: 'blob' // Important for file downloads
        }
      );

      if (response.ok) {
        // Create blob URL and trigger download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setErrors(prev => ({
        ...prev,
        download: 'Failed to download file'
      }));
    }
  };

  const renderFileDisplay = (fileUrl, fileName, fieldName) => {
    if (!fileUrl) return null;

    const displayName = fileName || fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

    return (
      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-green-800 font-medium">
              {displayName} uploaded
            </span>
          </div>
          <button
            type="button"
            onClick={() => handleFileDownload(fileUrl, displayName)}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Download
          </button>
        </div>
      </div>
    );
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;

    // Prevent changes if form is locked and user is not admin
    if (isFormLocked && !isAdmin) {
      return;
    }

    setLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/upload/${field}`,
        formDataUpload
      );
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          [field]: data.fileUrl
        }));
        // setSuccess(`${field} uploaded successfully!`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrors(prev => ({
        ...prev,
        [field]: 'Failed to upload file'
      }));
    }
    setLoading(false);
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      // Validate founder information
      foundersArray.forEach((founder, index) => {
        if (!founder.email.trim()) {
          newErrors[`founder_${index}_email`] = 'Email is required';
        }

        // For non-invited founders (first founder), require all fields
        if (!founder.isInvited) {
          if (!founder.fullName.trim()) {
            newErrors[`founder_${index}_name`] = 'Full name is required';
          }
          if (!founder.cellPhone.trim()) {
            newErrors[`founder_${index}_phone`] = 'Cell phone is required';
          }
          if (!founder.homeAddress.trim()) {
            newErrors[`founder_${index}_address`] = 'Home address is required';
          }
          if (!founder.dateOfBirth) {
            newErrors[`founder_${index}_dob`] = 'Date of birth is required';
          }
          if (!founder.ssn || !founder.ssn.trim()) {
            newErrors[`founder_${index}_ssn`] = 'Social Security Number is required';
          }
        }
      });
    }
    
    if (step === 2 && formData.companyAlreadyFormed === null) {
      newErrors.companyAlreadyFormed = 'Please select whether your company is already formed';
    }
    
    if (step === 3 && formData.companyAlreadyFormed === true) {
      // Validate existing company information
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      }
      if (!formData.dateOfFormation) {
        newErrors.dateOfFormation = 'Date of formation is required';
      }
      if (!formData.einNumber.trim()) {
        newErrors.einNumber = 'EIN number is required';
      }
      if (!formData.businessAddress.trim()) {
        newErrors.businessAddress = 'Business address is required';
      }
      if (!formData.businessDescription.trim()) {
        newErrors.businessDescription = 'Business description is required';
      }
      if (!formData.naicsCodes.trim()) {
        newErrors.naicsCodes = 'NAICS code is required';
      }
      if (!formData.preferredDomains[0].trim()) {
        newErrors.preferredDomains = 'At least one preferred domain is required';
      }
      if (!formData.certificateOfFormation) {
        newErrors.certificateOfFormation = 'Certificate of formation document is required';
      }
      if (!formData.ss4Document) {
        newErrors.ss4Document = 'SS-4 document is required';
      }
      if (formData.taxReturnsFiledPreviously === null) {
        newErrors.taxReturnsFiledPreviously = 'Please specify whether tax returns have been filed previously';
      }

      // Validate bank account information if company has one
      if (formData.hasBankAccount === true) {
        if (!formData.bankInfo.bankName.trim()) {
          newErrors.bankName = 'Bank name is required';
        }
        if (!formData.bankInfo.routingNumber.trim()) {
          newErrors.routingNumber = 'Routing number is required';
        }
        if (!formData.bankInfo.accountNumber.trim()) {
          newErrors.accountNumber = 'Account number is required';
        }
        if (!formData.bankInfo.bankPhone.trim()) {
          newErrors.bankPhone = 'Bank phone number is required';
        }
        if (!formData.bankInfo.bankAddress.trim()) {
          newErrors.bankAddress = 'Bank address is required';
        }
      } else if (formData.hasBankAccount === null) {
        newErrors.hasBankAccount = 'Please specify whether the company has an existing bank account';
      }
    }

    if (step === 3 && formData.companyAlreadyFormed === false) {
      // Validate new company information
      if (!formData.desiredCompanyName.trim()) {
        newErrors.desiredCompanyName = 'Desired company name is required';
      }
      if (!formData.newCompanyBusinessAddress.trim()) {
        newErrors.newCompanyBusinessAddress = 'Business address is required';
      }
      if (!formData.newCompanyBusinessDescription.trim()) {
        newErrors.newCompanyBusinessDescription = 'Business description is required';
      }
      if (!formData.newCompanyNaicsCodes.trim()) {
        newErrors.newCompanyNaicsCodes = 'NAICS code is required';
      }
      if (!formData.newCompanyPreferredDomains[0].trim()) {
        newErrors.newCompanyPreferredDomains = 'At least one preferred domain is required';
      }

      // No bank account validation for new companies - they don't have a company yet
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Ensure the business address is populated from the first founder's home
  // address on the new-company path when the user hasn't chosen a different one,
  // so it is never persisted empty regardless of the order steps were completed.
  const applyDefaultBusinessAddress = (data) => {
    if (data.companyAlreadyFormed !== false || showDifferentBusinessAddress) return data;
    const hasBiz = [
      data.newCompanyBusinessAddressLine1,
      data.newCompanyBusinessAddressCity,
      data.newCompanyBusinessAddressState,
      data.newCompanyBusinessAddressZip
    ].some(v => (v || '').trim());
    if (hasBiz) return data;
    const first = (data.founders && data.founders[0]) || {};
    return {
      ...data,
      newCompanyBusinessAddressLine1: first.homeAddressLine1 || '',
      newCompanyBusinessAddressLine2: first.homeAddressLine2 || '',
      newCompanyBusinessAddressCity: first.homeAddressCity || '',
      newCompanyBusinessAddressState: first.homeAddressState || '',
      newCompanyBusinessAddressZip: first.homeAddressZip || '',
      newCompanyBusinessAddress: combineAddress({
        line1: first.homeAddressLine1 || '',
        line2: first.homeAddressLine2 || '',
        city: first.homeAddressCity || '',
        state: first.homeAddressState || '',
        zip: first.homeAddressZip || ''
      })
    };
  };

  // Wrapper function to show email confirmation modal before submitting
  const handleSaveAsDraft = async () => {
    setLoading(true);
    try {
      // Create a clean copy of formData without file objects for draft
      const draftData = applyDefaultBusinessAddress({
        ...formData,
        isDraft: true, // Mark as draft
        // Ensure file fields are URLs (strings) not file objects
        certificateOfFormation: typeof formData.certificateOfFormation === 'string' ? formData.certificateOfFormation : null,
        ss4Document: typeof formData.ss4Document === 'string' ? formData.ss4Document : null,
        boirDocument: typeof formData.boirDocument === 'string' ? formData.boirDocument : null
      });

      const endpoint = submissionId
        ? `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/save-draft/${submissionId}`
        : `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/save-draft`;

      const response = await api.post(endpoint, draftData);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Draft saved:', data);

        // Store submission ID if this is a new draft
        if (!submissionId && data.submissionId) {
          setSubmissionId(data.submissionId);
        }

        setSuccess('Draft saved successfully! You can continue editing later.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setErrors({ ...errors, save: errorData.error || 'Failed to save draft' });
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setErrors({ ...errors, save: 'Failed to save draft. Please try again.' });
    }
    setLoading(false);
  };

  const handleSubmitWithConfirmation = async () => {
    if (!validateStep(currentStep)) return;

    // Determine if this submission will send emails
    let needsConfirmation = false;
    let recipients = [];
    let description = '';
    let additionalInfo = '';

    // Check for founder personal info submission (emails sent to admins when all founders submitted)
    let updatedFounders = [...foundersArray];
    let isFounderPersonalInfoSubmission = false;

    if (userInfo?.accountType === 'founder') {
      const currentUserEmail = userInfo?.email || userInfo?.username;
      const founderIndex = updatedFounders.findIndex(f => f.email === currentUserEmail);

      console.log("Checking for founder personal info submission:", { founderIndex, updatedFounders, submissionId });

      if (founderIndex !== -1 && updatedFounders[founderIndex].isInvited && submissionId) {
        updatedFounders[founderIndex] = {
          ...updatedFounders[founderIndex],
          isInvited: false
        };
        isFounderPersonalInfoSubmission = true;

        console.log("Founder personal info submission detected. Updated founders:", updatedFounders);

        // Check if all founders will have submitted after this submission
        const allFoundersWillBeSubmitted = updatedFounders.every(f => !f.isInvited);

        if (allFoundersWillBeSubmitted) {
          needsConfirmation = true;
          // Get admin emails (we'll need to fetch them, but for now show placeholder)
          description = 'Submitting your personal information will notify administrators that all founders have completed their information.';
          additionalInfo = 'Administrators will be notified via email to begin processing your company formation.';
          recipients = [{ email: 'Assigned Administrators', role: 'Admin Team' }];
        }
      }
    }

    // Check for full form submission with invited founders (invitations will be sent)
    if (!isFounderPersonalInfoSubmission && !isAdmin) {
      const foundersForInvitations = foundersArray;
      const invitedFounders = foundersForInvitations.filter(f => f.isInvited && f.email);

      if (invitedFounders.length > 0) {
        needsConfirmation = true;
        description = 'Submitting the company formation form will automatically send invitation emails to the founders listed below.';
        additionalInfo = 'Invited founders will receive instructions to complete their personal information.';
        recipients = invitedFounders.map(f => ({
          email: f.email,
          role: 'Founder'
        }));
      }
    }

    // If email confirmation is needed, show modal
    if (needsConfirmation && !isAdmin) {
      setEmailConfirmation({
        isOpen: true,
        emailData: {
          recipients,
          description,
          additionalInfo
        },
        onConfirm: () => {
          setEmailConfirmation({ isOpen: false, emailData: null, onConfirm: null });
          handleSubmit();
        }
      });
    } else {
      // No confirmation needed, submit directly
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    try {
      // If this is a founder submitting personal info, mark them as no longer invited
      let updatedFounders = [...foundersArray];
      let isFounderPersonalInfoSubmission = false;

      if (userInfo?.accountType === 'founder') {
        const currentUserEmail = userInfo?.email || userInfo?.username;
        const founderIndex = updatedFounders.findIndex(f => f.email === currentUserEmail);

        // Only mark as personal info submission if:
        // 1. The founder exists in the list AND is marked as invited
        // 2. AND there's already a submission (meaning it's a subsequent submission, not the initial one)
        if (founderIndex !== -1 && updatedFounders[founderIndex].isInvited && submissionId) {
          updatedFounders[founderIndex] = {
            ...updatedFounders[founderIndex],
            isInvited: false // Mark as completed their personal info
          };
          isFounderPersonalInfoSubmission = true;
        }
      }

      // Create a clean copy of formData without file objects
      const submitData = applyDefaultBusinessAddress({
        ...formData,
        founders: updatedFounders, // Use updated founders list
        // Ensure file fields are URLs (strings) not file objects
        certificateOfFormation: typeof formData.certificateOfFormation === 'string' ? formData.certificateOfFormation : null,
        ss4Document: typeof formData.ss4Document === 'string' ? formData.ss4Document : null,
        boirDocument: typeof formData.boirDocument === 'string' ? formData.boirDocument : null
      });

      console.log("isFounderPerseonalInfoSubmission:", isFounderPersonalInfoSubmission);

      // Use different endpoint for founder personal info updates
      const endpoint = isFounderPersonalInfoSubmission
        ? `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/update-founder-info/${submissionId}`
        : `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/submit`;

      const response = await api.post(endpoint, submitData);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Submission response:', data);

        // For founder personal info updates, maintain the existing submissionId
        if (!isFounderPersonalInfoSubmission) {
          setSubmissionId(data.submissionId);
        }
        setIsFormLocked(true);

        // Update form data with the changes made during submission
        setFormData(prev => ({
          ...prev,
          founders: updatedFounders
        }));

        // For invitation sending, use original founders list if this is a founder personal info submission
        // Otherwise use updated founders list
        const foundersForInvitations = isFounderPersonalInfoSubmission ? foundersArray : updatedFounders;
        const invitedFounders = foundersForInvitations.filter(f => f.isInvited && f.email);

        // Different success messages based on submission type
        if (isAdmin) {
          setSuccess('Company formation information updated successfully!');
        } else if (isFounderPersonalInfoSubmission) {
          setSuccess('Your personal information has been submitted successfully! This information is now visible to administrators and other founders.');
        } else {
          // Auto-send invitations if there are invited founders (for initial submissions)
          if (invitedFounders.length > 0) {
            try {
              setSendingInvitations(true);
              const inviteResponse = await api.post(
                `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/send-invitations/${data.submissionId}`
              );

              if (inviteResponse.ok) {
                const inviteData = await inviteResponse.json();
                setSuccess(`Company formation information submitted successfully! Invitation emails have been automatically sent to ${inviteData.invitedFounders.join(', ')}. They will receive instructions to complete their personal information. The form is now locked but viewable. Contact admin@streamlaunch.com to make changes.`);
              } else {
                setSuccess(`Company formation information submitted successfully! However, there was an issue sending invitation emails to ${invitedFounders.length} founder(s). Please contact admin@streamlaunch.com for assistance. The form is now locked but viewable.`);
              }
              setSendingInvitations(false);
            } catch (inviteError) {
              console.error('Error auto-sending invitations:', inviteError);
              setSuccess(`Company formation information submitted successfully! However, there was an issue sending invitation emails to ${invitedFounders.length} founder(s). Please contact admin@streamlaunch.com for assistance. The form is now locked but viewable.`);
              setSendingInvitations(false);
            }
          } else {
            setSuccess('Company formation information submitted successfully! We will begin processing your information. The form is now locked but viewable. Contact admin@streamlaunch.com to make changes.');
          }
        }
      } else {
        const errorData = await response.json();
        setErrors({ submit: errorData.error || 'Failed to submit information' });
      }
    } catch (error) {
      console.error('Error submitting company formation:', error);
      setErrors({ submit: 'Failed to submit information' });
    }
    setLoading(false);
  };

  const handleSendInvitations = async () => {
    if (!submissionId) return;

    setSendingInvitations(true);
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/send-invitations/${submissionId}`
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Invitations sent successfully to ${data.invitedFounders.join(', ')}! They will receive emails with instructions to complete their personal information.`);
      } else {
        const errorData = await response.json();
        setErrors({ invite: errorData.error || 'Failed to send invitations' });
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      setErrors({ invite: 'Failed to send invitations' });
    }
    setSendingInvitations(false);
  };

  const renderFounderInformation = () => {
    // Identify which founder is the current user
    const currentUserEmail = userInfo?.email || userInfo?.username;
    console.log("currentUserEmail:", currentUserEmail);
    const getCurrentFounderIndex = () => {
      return foundersArray.findIndex(f => f.email === currentUserEmail);
    };

    // Check if current founder has submitted their personal info
    console.log("renderFounderinfo formdata.founders:", foundersArray);
    console.log("typeof formData.founders:", typeof formData.founders);
    const currentFounder = foundersArray.find(f => f.email === currentUserEmail);
    const hasSubmittedPersonalInfo = currentFounder &&
                                      submissionStatus === 'submitted' &&
                                      !currentFounder.isInvited && // Only lock after they've clicked submit
                                      currentFounder.fullName &&
                                      currentFounder.cellPhone &&
                                      currentFounder.dateOfBirth &&
                                      currentFounder.homeAddress &&
                                      currentFounder.ssn;

    return (
      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Founder Information</h3>
          <p className="text-gray-600">Please provide information for all founders of the company.</p>
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Additional founders can be added later through the User Management section of your dashboard.
            </p>
          </div>
        </div>

{/* For invited founders, show only their own blank form + summary of other founders */}
        {userInfo?.accountType === 'founder' && submissionId ? (
          <>
            {/* Current founder's editable form */}
            {foundersArray.map((founder, index) => {
              const isCurrentUser = founder.email === currentUserEmail;
              if (!isCurrentUser) return null; // Only show current user's form

              return (
                <div key={index} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h4 className="text-md font-medium text-gray-900">
                        Your Personal Information
                      </h4>
                      <p className="text-sm text-blue-600">
                        Please complete your personal information below
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={founder.fullName}
                        onChange={(e) => handleFounderChange(index, 'fullName', e.target.value)}
                        className="input-field"
                        disabled={hasSubmittedPersonalInfo || (isAdmin && !adminEditMode)}
                      />
                      {errors[`founder_${index}_name`] && (
                        <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_name`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={founder.email}
                        className="input-field"
                        disabled={true} // Founders can't edit their own email
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cell Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={founder.cellPhone}
                        onChange={(e) => handleFounderChange(index, 'cellPhone', e.target.value)}
                        className="input-field"
                        placeholder="(555) 123-4567"
                        disabled={hasSubmittedPersonalInfo || (isAdmin && !adminEditMode)}
                      />
                      {errors[`founder_${index}_phone`] && (
                        <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_phone`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        value={founder.dateOfBirth}
                        onChange={(e) => handleFounderChange(index, 'dateOfBirth', e.target.value)}
                        className="input-field"
                        disabled={hasSubmittedPersonalInfo || (isAdmin && !adminEditMode)}
                      />
                      {errors[`founder_${index}_dob`] && (
                        <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_dob`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Social Security Number (SSN) *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={ssnVisible[`founder_${index}`] ? "text" : "password"}
                          value={founder.ssn || ''}
                          onChange={(e) => handleFounderChange(index, 'ssn', e.target.value)}
                          className="input-field"
                          placeholder="XXX-XX-XXXX"
                          maxLength="11"
                          style={{ paddingRight: '40px' }}
                          disabled={hasSubmittedPersonalInfo || (isAdmin && !adminEditMode)}
                        />
                        <button
                          type="button"
                          onClick={() => setSsnVisible(prev => ({ ...prev, [`founder_${index}`]: !prev[`founder_${index}`] }))}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          aria-label={ssnVisible[`founder_${index}`] ? "Hide SSN" : "Show SSN"}
                        >
                          {ssnVisible[`founder_${index}`] ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"></path>
                              <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          )}
                        </button>
                      </div>
                      {errors[`founder_${index}_ssn`] && (
                        <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_ssn`]}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Home Address *
                      </label>
                      <AddressFieldGroup
                        prefix="homeAddress"
                        values={{
                          line1: founder.homeAddressLine1 || '',
                          line2: founder.homeAddressLine2 || '',
                          city: founder.homeAddressCity || '',
                          state: founder.homeAddressState || '',
                          zip: founder.homeAddressZip || ''
                        }}
                        onChange={(field, value) => handleFounderChange(index, field, value)}
                        disabled={hasSubmittedPersonalInfo || (isAdmin && !adminEditMode)}
                        required
                      />
                      {errors[`founder_${index}_address`] && (
                        <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_address`]}</p>
                      )}
                      {index === 0 && formData.companyAlreadyFormed === false && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-sm text-blue-800">
                            <span className="font-medium">Note:</span> If you don't have a company formed already, your home address will be used as the business address.
                          </p>
                          <button
                            type="button"
                            onClick={() => toggleDifferentBusinessAddress()}
                            className="text-sm text-blue-600 hover:text-blue-800 underline mt-1"
                            disabled={hasSubmittedPersonalInfo || (isAdmin && !adminEditMode)}
                          >
                            {showDifferentBusinessAddress ? 'Use home address as business address' : 'Use a different business address'}
                          </button>
                          {showDifferentBusinessAddress && (
                            <div className="mt-3">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Different Business Address
                              </label>
                              <AddressFieldGroup
                                prefix="newCompanyBusinessAddress"
                                values={{
                                  line1: formData.newCompanyBusinessAddressLine1 || '',
                                  line2: formData.newCompanyBusinessAddressLine2 || '',
                                  city: formData.newCompanyBusinessAddressCity || '',
                                  state: formData.newCompanyBusinessAddressState || '',
                                  zip: formData.newCompanyBusinessAddressZip || ''
                                }}
                                onChange={handleInputChange}
                                disabled={hasSubmittedPersonalInfo || (isAdmin && !adminEditMode)}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Summary of other founders */}
            {foundersArray.filter(f => f.email !== currentUserEmail).length > 0 && (
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <h4 className="text-md font-medium text-gray-900 mb-4">Other Founders</h4>
                <div className="space-y-3">
                  {foundersArray.map((founder, index) => {
                    const isCurrentUser = founder.email === currentUserEmail;
                    if (isCurrentUser) return null;

                    return (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                        <div>
                          <p className="font-medium text-gray-900">
                            {founder.fullName || 'Pending submission'}
                          </p>
                          <p className="text-sm text-gray-600">{founder.email}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          founder.isInvited
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {founder.isInvited ? 'Invited' : 'Submitted'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Original founder mapping for non-founder users or unlocked forms */
          foundersArray.map((founder, index) => {
            const isCurrentUser = founder.email === currentUserEmail;
            // Admins are read-only until they explicitly enter edit mode.
    const canEditFounder = !isFormLocked || (isAdmin && adminEditMode) || (userInfo?.accountType === 'founder' && isCurrentUser);

            return (
              <div key={index} className="border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900">
                      Founder {index + 1}
                      {isCurrentUser && " (You)"}
                      {founder.isInvited && !isCurrentUser && " (Invited)"}
                    </h4>
                    {founder.isInvited && !isCurrentUser && (
                      <p className="text-sm text-gray-500">
                        Invited founder will complete their personal info after receiving invitation
                      </p>
                    )}
                    {isCurrentUser && userInfo?.accountType === 'founder' && (
                      <p className="text-sm text-blue-600">
                        Please complete your personal information below
                      </p>
                    )}
                  </div>
                  {index > 0 && canEditFounder && !isCurrentUser && (
                    <button
                      onClick={() => removeFounder(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                      disabled={!canEditFounder}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(!founder.isInvited || isCurrentUser) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={founder.fullName}
                        onChange={(e) => handleFounderChange(index, 'fullName', e.target.value)}
                        className="input-field"
                        disabled={!canEditFounder}
                      />
                      {errors[`founder_${index}_name`] && (
                        <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_name`]}</p>
                      )}
                    </div>
                  )}

                  <div className={founder.isInvited && !isCurrentUser ? "md:col-span-2" : ""}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={founder.email}
                      onChange={(e) => handleFounderChange(index, 'email', e.target.value)}
                      className="input-field"
                      disabled={!canEditFounder || (isCurrentUser && userInfo?.accountType === 'founder')} // Founders can't edit their own email
                      placeholder={founder.isInvited && !isCurrentUser ? "Enter email address to send invitation" : ""}
                    />
                    {errors[`founder_${index}_email`] && (
                      <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_email`]}</p>
                    )}
                  </div>

                  {(!founder.isInvited || isCurrentUser) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cell Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={founder.cellPhone}
                          onChange={(e) => handleFounderChange(index, 'cellPhone', e.target.value)}
                          className="input-field"
                          placeholder="(555) 123-4567"
                          disabled={!canEditFounder}
                        />
                        {errors[`founder_${index}_phone`] && (
                          <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_phone`]}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date of Birth *
                        </label>
                        <input
                          type="date"
                          value={founder.dateOfBirth}
                          onChange={(e) => handleFounderChange(index, 'dateOfBirth', e.target.value)}
                          className="input-field"
                          disabled={!canEditFounder}
                        />
                        {errors[`founder_${index}_dob`] && (
                          <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_dob`]}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Social Security Number (SSN) *
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={ssnVisible[`founder_${index}_admin`] ? "text" : "password"}
                            value={founder.ssn || ''}
                            onChange={(e) => handleFounderChange(index, 'ssn', e.target.value)}
                            className="input-field"
                            placeholder="XXX-XX-XXXX"
                            maxLength="11"
                            disabled={!canEditFounder}
                            style={{ paddingRight: '40px' }}
                          />
                          <button
                            type="button"
                            onClick={() => setSsnVisible(prev => ({ ...prev, [`founder_${index}_admin`]: !prev[`founder_${index}_admin`] }))}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            aria-label={ssnVisible[`founder_${index}_admin`] ? "Hide SSN" : "Show SSN"}
                          >
                            {ssnVisible[`founder_${index}_admin`] ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"></path>
                                <line x1="1" y1="1" x2="23" y2="23"></line>
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            )}
                          </button>
                        </div>
                        {errors[`founder_${index}_ssn`] && (
                          <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_ssn`]}</p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Home Address *
                        </label>
                        <AddressFieldGroup
                          prefix="homeAddress"
                          values={{
                            line1: founder.homeAddressLine1 || '',
                            line2: founder.homeAddressLine2 || '',
                            city: founder.homeAddressCity || '',
                            state: founder.homeAddressState || '',
                            zip: founder.homeAddressZip || ''
                          }}
                          onChange={(field, value) => handleFounderChange(index, field, value)}
                          disabled={!canEditFounder}
                          required
                        />
                        {errors[`founder_${index}_address`] && (
                          <p className="text-red-600 text-sm mt-1">{errors[`founder_${index}_address`]}</p>
                        )}
                        {index === 0 && formData.companyAlreadyFormed === false && (
                          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">Note:</span> If you don't have a company formed already, your home address will be used as the business address.
                            </p>
                            <button
                              type="button"
                              onClick={() => toggleDifferentBusinessAddress()}
                              className="text-sm text-blue-600 hover:text-blue-800 underline mt-1"
                              disabled={!canEditFounder}
                            >
                              {showDifferentBusinessAddress ? 'Use home address as business address' : 'Use a different business address'}
                            </button>
                            {showDifferentBusinessAddress && (
                              <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Different Business Address
                                </label>
                                <AddressFieldGroup
                                  prefix="newCompanyBusinessAddress"
                                  values={{
                                    line1: formData.newCompanyBusinessAddressLine1 || '',
                                    line2: formData.newCompanyBusinessAddressLine2 || '',
                                    city: formData.newCompanyBusinessAddressCity || '',
                                    state: formData.newCompanyBusinessAddressState || '',
                                    zip: formData.newCompanyBusinessAddressZip || ''
                                  }}
                                  onChange={handleInputChange}
                                  disabled={!canEditFounder}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}

{/* Only show Add Founder button before initial submission or for admins */}
        {(!submissionId || isAdmin) && (
          <button
            onClick={addFounder}
            className="btn-secondary"
            disabled={!isAdmin && isFormLocked}
          >
            + Add Another Founder
          </button>
        )}
      </div>
    );
  };

  const renderCompanyStatusQuestion = () => (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Company Formation Status</h3>
        <p className="text-gray-600">Please let us know if you have already formed a company.</p>
      </div>
      
      <div className="space-y-4">
        <label className="flex items-center space-x-3">
          <input
            type="radio"
            name="companyAlreadyFormed"
            value="true"
            checked={formData.companyAlreadyFormed === true}
            onChange={() => handleInputChange('companyAlreadyFormed', true)}
            className="form-radio"
            disabled={isInputDisabled()}
          />
          <span className="text-gray-900">Yes, we have already formed a company</span>
        </label>
        
        <label className="flex items-center space-x-3">
          <input
            type="radio"
            name="companyAlreadyFormed"
            value="false"
            checked={formData.companyAlreadyFormed === false}
            onChange={() => handleInputChange('companyAlreadyFormed', false)}
            className="form-radio"
            disabled={isInputDisabled()}
          />
          <span className="text-gray-900">No, we need help forming a company</span>
        </label>
      </div>
      
      {errors.companyAlreadyFormed && (
        <p className="text-red-600 text-sm">{errors.companyAlreadyFormed}</p>
      )}
    </div>
  );

  const renderExistingCompanyForm = () => (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Existing Company Information</h3>
        <p className="text-gray-600">Please provide details about your existing company.</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name *
          </label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            className="input-field"
            disabled={isInputDisabled()}
          />
          {errors.companyName && (
            <p className="text-red-600 text-sm mt-1">{errors.companyName}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date of Formation *
          </label>
          <input
            type="date"
            value={formData.dateOfFormation}
            onChange={(e) => handleInputChange('dateOfFormation', e.target.value)}
            className="input-field"
            disabled={isInputDisabled()}
          />
          {errors.dateOfFormation && (
            <p className="text-red-600 text-sm mt-1">{errors.dateOfFormation}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            EIN Number *
          </label>
          <input
            type="text"
            value={formData.einNumber}
            onChange={(e) => handleInputChange('einNumber', e.target.value)}
            className="input-field"
            placeholder="XX-XXXXXXX"
            disabled={isInputDisabled()}
          />
          {errors.einNumber && (
            <p className="text-red-600 text-sm mt-1">{errors.einNumber}</p>
          )}
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Address *
        </label>
        {(() => {
          const primaryFounder = (formData.founders && formData.founders[0]) || {};
          const homeParts = {
            line1: primaryFounder.homeAddressLine1 || '',
            line2: primaryFounder.homeAddressLine2 || '',
            city: primaryFounder.homeAddressCity || '',
            state: primaryFounder.homeAddressState || '',
            zip: primaryFounder.homeAddressZip || ''
          };
          const businessParts = {
            line1: formData.businessAddressLine1 || '',
            line2: formData.businessAddressLine2 || '',
            city: formData.businessAddressCity || '',
            state: formData.businessAddressState || '',
            zip: formData.businessAddressZip || ''
          };
          const sameAsHome =
            !!homeParts.line1 &&
            homeParts.line1 === businessParts.line1 &&
            homeParts.line2 === businessParts.line2 &&
            homeParts.city === businessParts.city &&
            homeParts.state === businessParts.state &&
            homeParts.zip === businessParts.zip;
          return (
            <label className="flex items-center gap-2 mb-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={sameAsHome}
                disabled={isInputDisabled()}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleInputChange('businessAddressLine1', homeParts.line1);
                    handleInputChange('businessAddressLine2', homeParts.line2);
                    handleInputChange('businessAddressCity', homeParts.city);
                    handleInputChange('businessAddressState', homeParts.state);
                    handleInputChange('businessAddressZip', homeParts.zip);
                    handleInputChange('businessAddress', primaryFounder.homeAddress || '');
                  } else {
                    handleInputChange('businessAddressLine1', '');
                    handleInputChange('businessAddressLine2', '');
                    handleInputChange('businessAddressCity', '');
                    handleInputChange('businessAddressState', '');
                    handleInputChange('businessAddressZip', '');
                    handleInputChange('businessAddress', '');
                  }
                }}
              />
              Same as home address
            </label>
          );
        })()}
        <AddressFieldGroup
          prefix="businessAddress"
          values={{
            line1: formData.businessAddressLine1 || '',
            line2: formData.businessAddressLine2 || '',
            city: formData.businessAddressCity || '',
            state: formData.businessAddressState || '',
            zip: formData.businessAddressZip || ''
          }}
          onChange={handleInputChange}
          disabled={isInputDisabled()}
          required
        />
        {errors.businessAddress && (
          <p className="text-red-600 text-sm mt-1">{errors.businessAddress}</p>
        )}
        <p className="text-sm text-gray-500 mt-1">
          Often for early-stage startups, this is the founder's home address.
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Description *
        </label>
        <textarea
          value={formData.businessDescription}
          onChange={(e) => handleInputChange('businessDescription', e.target.value)}
          className="input-field"
          rows="2"
          placeholder="Brief description of your business (1-2 sentences)"
          disabled={isInputDisabled()}
        />
        {errors.businessDescription && (
          <p className="text-red-600 text-sm mt-1">{errors.businessDescription}</p>
        )}
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          NAICS Code(s) *
        </label>
        <input
          type="text"
          value={formData.naicsCodes}
          onChange={(e) => handleInputChange('naicsCodes', e.target.value)}
          className="input-field"
          placeholder="e.g., 541715"
          disabled={isInputDisabled()}
        />
        {errors.naicsCodes && (
          <p className="text-red-600 text-sm mt-1">{errors.naicsCodes}</p>
        )}
        <p className="text-sm text-gray-500 mt-1">
          If unknown, please perform a keyword search <a href="https://www.naics.com/search/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">here</a>
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Preferred Web/Email Domain *
        </label>
        <p className="text-sm text-gray-500 mb-3">Please provide several options in order of preference:</p>
        <div className="space-y-2">
          {formData.preferredDomains.map((domain, index) => (
            <div key={index} className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 w-16">Option {index + 1}:</span>
              <input
                type="text"
                value={domain}
                onChange={(e) => handleDomainChange(index, e.target.value)}
                className="input-field flex-1"
                placeholder="example.com"
                disabled={isInputDisabled()}
              />
            </div>
          ))}
        </div>
        {errors.preferredDomains && (
          <p className="text-red-600 text-sm mt-1">{errors.preferredDomains}</p>
        )}
      </div>
      
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Have tax returns been filed previously? *
        </label>
        <div className="space-y-2">
          <label className="flex items-center space-x-3">
            <input
              type="radio"
              name="taxReturnsFiledPreviously"
              value="true"
              checked={formData.taxReturnsFiledPreviously === true}
              onChange={() => handleInputChange('taxReturnsFiledPreviously', true)}
              className="form-radio"
              disabled={isInputDisabled()}
            />
            <span className="text-gray-900">Yes</span>
          </label>
          
          <label className="flex items-center space-x-3">
            <input
              type="radio"
              name="taxReturnsFiledPreviously"
              value="false"
              checked={formData.taxReturnsFiledPreviously === false}
              onChange={() => handleInputChange('taxReturnsFiledPreviously', false)}
              className="form-radio"
              disabled={isInputDisabled()}
            />
            <span className="text-gray-900">No</span>
          </label>
        </div>
        {errors.taxReturnsFiledPreviously && (
          <p className="text-red-600 text-sm mt-1">{errors.taxReturnsFiledPreviously}</p>
        )}
      </div>

      {/* Bank Account Existence Question */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Banking Information</h4>
        <p className="text-gray-600 mb-4">Is there an existing bank account for the company? *</p>
        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              type="radio"
              checked={formData.hasBankAccount === true}
              onChange={() => handleInputChange('hasBankAccount', true)}
              className="form-radio"
              disabled={isInputDisabled()}
            />
            <span className="text-gray-900">Yes</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={formData.hasBankAccount === false}
              onChange={() => handleInputChange('hasBankAccount', false)}
              className="form-radio"
              disabled={isInputDisabled()}
            />
            <span className="text-gray-900">No</span>
          </label>
        </div>
        {errors.hasBankAccount && (
          <p className="text-red-600 text-sm mt-1">{errors.hasBankAccount}</p>
        )}
      </div>

      {/* Bank Account Information - Only show if has bank account */}
      {formData.hasBankAccount === true && (
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Bank Account Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Name *
            </label>
            <input
              type="text"
              value={formData.bankInfo.bankName}
              onChange={(e) => handleBankInfoChange('bankName', e.target.value)}
              className="input-field"
              disabled={isInputDisabled()}
            />
            {errors.bankName && (
              <p className="text-red-600 text-sm mt-1">{errors.bankName}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Routing Number *
            </label>
            <input
              type="text"
              value={formData.bankInfo.routingNumber}
              onChange={(e) => handleBankInfoChange('routingNumber', e.target.value)}
              className="input-field"
              disabled={isInputDisabled()}
            />
            {errors.routingNumber && (
              <p className="text-red-600 text-sm mt-1">{errors.routingNumber}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Number *
            </label>
            <input
              type="text"
              value={formData.bankInfo.accountNumber}
              onChange={(e) => handleBankInfoChange('accountNumber', e.target.value)}
              className="input-field"
              disabled={isInputDisabled()}
            />
            {errors.accountNumber && (
              <p className="text-red-600 text-sm mt-1">{errors.accountNumber}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Phone Number *
            </label>
            <input
              type="tel"
              value={formData.bankInfo.bankPhone}
              onChange={(e) => handleBankInfoChange('bankPhone', e.target.value)}
              className="input-field"
              disabled={isInputDisabled()}
            />
            {errors.bankPhone && (
              <p className="text-red-600 text-sm mt-1">{errors.bankPhone}</p>
            )}
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Address *
            </label>
            <textarea
              value={formData.bankInfo.bankAddress}
              onChange={(e) => handleBankInfoChange('bankAddress', e.target.value)}
              className="input-field"
              rows="3"
              disabled={isInputDisabled()}
            />
            {errors.bankAddress && (
              <p className="text-red-600 text-sm mt-1">{errors.bankAddress}</p>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Document Uploads */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Required Documents</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Certificate of Formation * (Required)
            </label>
            <input
              type="file"
              onChange={(e) => handleFileUpload('certificateOfFormation', e.target.files[0])}
              className="input-field"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              disabled={isInputDisabled()}
            />
            {errors.certificateOfFormation && (
              <p className="text-red-600 text-sm mt-1">{errors.certificateOfFormation}</p>
            )}
            {renderFileDisplay(formData.certificateOfFormation, 'Certificate of Formation', 'certificateOfFormation')}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SS-4 (Document with EIN number) * (Required)
            </label>
            <input
              type="file"
              onChange={(e) => handleFileUpload('ss4Document', e.target.files[0])}
              className="input-field"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              disabled={isInputDisabled()}
            />
            {errors.ss4Document && (
              <p className="text-red-600 text-sm mt-1">{errors.ss4Document}</p>
            )}
            {renderFileDisplay(formData.ss4Document, 'SS-4 Document', 'ss4Document')}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beneficial Ownership Information Report (BOIR) (Optional)
            </label>
            <input
              type="file"
              onChange={(e) => handleFileUpload('boirDocument', e.target.files[0])}
              className="input-field"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              disabled={isInputDisabled()}
            />
            {renderFileDisplay(formData.boirDocument, 'BOIR Document', 'boirDocument')}
            <p className="text-sm text-gray-500 mt-1">This document is optional if you don't have it yet.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNewCompanyForm = () => (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">New Company Information</h3>
        <p className="text-gray-600">Please provide details for your new company formation.</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Desired Company Name *
        </label>
        <input
          type="text"
          value={formData.desiredCompanyName}
          onChange={(e) => handleInputChange('desiredCompanyName', e.target.value)}
          className="input-field"
          disabled={isInputDisabled()}
        />
        {errors.desiredCompanyName && (
          <p className="text-red-600 text-sm mt-1">{errors.desiredCompanyName}</p>
        )}
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Address *
        </label>
        {formData.companyAlreadyFormed === false && (
          <button
            type="button"
            onClick={() => toggleDifferentBusinessAddress()}
            className="text-sm text-blue-600 hover:text-blue-800 underline mb-2 block"
            disabled={isInputDisabled()}
          >
            {showDifferentBusinessAddress ? 'Use home address as business address' : 'Use a different business address'}
          </button>
        )}
        <AddressFieldGroup
          prefix="newCompanyBusinessAddress"
          values={{
            line1: formData.newCompanyBusinessAddressLine1 || '',
            line2: formData.newCompanyBusinessAddressLine2 || '',
            city: formData.newCompanyBusinessAddressCity || '',
            state: formData.newCompanyBusinessAddressState || '',
            zip: formData.newCompanyBusinessAddressZip || ''
          }}
          onChange={handleInputChange}
          disabled={isInputDisabled() || (formData.companyAlreadyFormed === false && !showDifferentBusinessAddress)}
          required
        />
        {errors.newCompanyBusinessAddress && (
          <p className="text-red-600 text-sm mt-1">{errors.newCompanyBusinessAddress}</p>
        )}
        <p className="text-sm text-gray-500 mt-1">
          {formData.companyAlreadyFormed === false && !showDifferentBusinessAddress
            ? "Auto-filled from the first founder's home address. Choose 'Use a different business address' above to override."
            : "Often for early-stage startups, this is the founder's home address."}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Description *
        </label>
        <textarea
          value={formData.newCompanyBusinessDescription}
          onChange={(e) => handleInputChange('newCompanyBusinessDescription', e.target.value)}
          className="input-field"
          rows="2"
          placeholder="Brief description of your business (1-2 sentences)"
          disabled={isInputDisabled()}
        />
        {errors.newCompanyBusinessDescription && (
          <p className="text-red-600 text-sm mt-1">{errors.newCompanyBusinessDescription}</p>
        )}
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          NAICS Code(s) *
        </label>
        <input
          type="text"
          value={formData.newCompanyNaicsCodes}
          onChange={(e) => handleInputChange('newCompanyNaicsCodes', e.target.value)}
          className="input-field"
          placeholder="e.g., 541715"
          disabled={isInputDisabled()}
        />
        {errors.newCompanyNaicsCodes && (
          <p className="text-red-600 text-sm mt-1">{errors.newCompanyNaicsCodes}</p>
        )}
        <p className="text-sm text-gray-500 mt-1">
          If unknown, please perform a keyword search <a href="https://www.naics.com/search/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">here</a>
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Preferred Web/Email Domain *
        </label>
        <p className="text-sm text-gray-500 mb-3">Please provide several options in order of preference:</p>
        <div className="space-y-2">
          {formData.newCompanyPreferredDomains.map((domain, index) => (
            <div key={index} className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 w-16">Option {index + 1}:</span>
              <input
                type="text"
                value={domain}
                onChange={(e) => handleNewCompanyDomainChange(index, e.target.value)}
                className="input-field flex-1"
                placeholder="example.com"
                disabled={isInputDisabled()}
              />
            </div>
          ))}
        </div>
        {errors.newCompanyPreferredDomains && (
          <p className="text-red-600 text-sm mt-1">{errors.newCompanyPreferredDomains}</p>
        )}
      </div>
    </div>
  );

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return "Founder Information";
      case 2: return "Company Formation Status";
      case 3: return formData.companyAlreadyFormed ? "Existing Company Details" : "New Company Details";
      default: return "Company Formation";
    }
  };

  const getTotalSteps = () => {
    return 3;
  };

  if (!userInfo || (userInfo.accountType !== 'founder' && userInfo.accountType !== 'admin' && userInfo.accountType !== 'superadmin')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-center text-gray-600">This page is only available for founders and administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="card p-8">
          {/* Back link to return to the previous page */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>

          {/* Header with page number */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Company Formation Questionnaire</h1>
                {isFormLocked && (
                  <div className="mt-2 text-sm text-orange-600 font-medium">
                    {isAdmin ? 'Form submitted - Admin edit mode enabled' : 'Form submitted and locked - Contact admin@streamlaunch.com for changes'}
                  </div>
                )}
                {isAdmin && (
                  <div className="mt-2 text-sm font-medium">
                    {adminEditMode ? (
                      <span className="text-green-700">Edit mode enabled — changes can be saved.</span>
                    ) : (
                      <span className="text-gray-600">Read-only view. Click Edit to make changes.</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setAdminEditMode(prev => !prev)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      adminEditMode
                        ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {adminEditMode ? 'Lock' : 'Edit'}
                  </button>
                )}
                <div className="text-lg font-medium text-gray-600">
                  Page {currentStep} of {getTotalSteps()}
                </div>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-gray-800 mt-4">{getStepTitle()}</h2>
          </div>


          {/* Step content */}
          <div className="mb-8">
            {currentStep === 1 && renderFounderInformation()}
            {currentStep === 2 && renderCompanyStatusQuestion()}
            {currentStep === 3 && formData.companyAlreadyFormed === true && renderExistingCompanyForm()}
            {currentStep === 3 && formData.companyAlreadyFormed === false && renderNewCompanyForm()}
          </div>

          {/* Navigation */}
          {(!isFormLocked || isAdmin || userInfo?.accountType === 'founder') && (
            <div>
              <div className="flex justify-between items-center">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 1 || isNavigationDisabled()}
                  className={`btn-secondary ${currentStep === 1 || isNavigationDisabled() ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Previous
                </button>

                <div className="flex gap-3">
                  {/* Special submit button for invited founders on step 1 - only show if form is submitted and locked and personal info is NOT yet complete */}
                  {userInfo?.accountType === 'founder' && currentStep === 1 && submissionId && submissionStatus === 'submitted' && !isFounderPersonalInfoComplete() && (
                    <button
                      onClick={handleSubmitWithConfirmation}
                      disabled={loading}
                      className="btn-primary"
                    >
                      {loading ? 'Submitting...' : 'Submit Personal Information'}
                    </button>
                  )}

                  {/* Regular navigation buttons */}
                  {currentStep < getTotalSteps() ? (
                    // For founders on step 1 with submitted form, only show Next if they have completed their personal info
                    // For drafts or new forms, always show Next
                    !(userInfo?.accountType === 'founder' && currentStep === 1 && submissionId && submissionStatus === 'submitted' && isFormLocked && !isFounderPersonalInfoComplete()) && (
                      <button
                        onClick={nextStep}
                        disabled={loading || isNavigationDisabled()}
                        className="btn-primary"
                      >
                        {loading ? 'Loading...' : 'Next'}
                      </button>
                    )
                  ) : (
                    /* Only show final submit for non-founders or admins */
                    !(userInfo?.accountType === 'founder' && isFormLocked) && (
                      <>
                        <button
                          onClick={handleSaveAsDraft}
                          disabled={loading}
                          className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleSubmitWithConfirmation}
                          disabled={loading || isInputDisabled()}
                          className="btn-primary"
                        >
                          {loading ? 'Submitting...' : isFormLocked && isAdmin ? 'Update Information' : 'Submit'}
                        </button>
                      </>
                    )
                  )}
                </div>
              </div>

              {/* Note about form submission - moved below buttons */}
              {currentStep === getTotalSteps() && !(userInfo?.accountType === 'founder' && isFormLocked) && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> You can save your progress as a draft at any time. The form needs to be submitted to move to the next step in the company formation process.
                  </p>
                </div>
              )}
            </div>
          )}


          {/* Status Messages - Moved to bottom */}
          {success && (
            <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {errors.invite && (
            <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.invite}
            </div>
          )}

          {errors.submit && (
            <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.submit}
            </div>
          )}

          {errors.download && (
            <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.download}
            </div>
          )}

          {isFormLocked && !isAdmin && (
            <div className="text-center text-gray-600 mt-4">
              <p>Form has been submitted and is now read-only.</p>
              <p className="text-sm">Contact <a href="mailto:admin@streamlaunch.com" className="text-blue-600">admin@streamlaunch.com</a> to make changes.</p>
            </div>
          )}
        </div>

        {/* Email Confirmation Modal */}
        <EmailConfirmationModal
          isOpen={emailConfirmation.isOpen}
          onClose={() => setEmailConfirmation({ isOpen: false, emailData: null, onConfirm: null })}
          onConfirm={emailConfirmation.onConfirm}
          emailData={emailConfirmation.emailData}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default CompanyFormation;